import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, Mail, Phone, MapPin, Send, X } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const FooterSection = () => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const footerLinks = {
    services: [
      { name: "매칭 서비스", href: "#services" },
      { name: "커뮤니티", href: "#services" },
      { name: "코칭 프로그램", href: "#curriculum" },
      { name: "이벤트", href: "#services" },
    ],
    curriculum: [
      { name: "자존감학과", href: "#curriculum" },
      { name: "소통기술학과", href: "#curriculum" },
      { name: "연애학과", href: "#curriculum" },
      { name: "결혼학과", href: "#curriculum" },
    ],
    support: [
      { name: "자주 묻는 질문", href: "/faq" },
      { name: "고객센터", href: "/support" },
      { name: "이용약관", href: "/terms" },
      { name: "개인정보처리방침", href: "/privacy" },
    ],
  };

  const scrollToSection = (href) => {
    if (href.startsWith("#")) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    } else if (href.startsWith("/")) {
      navigate(href);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmitContact = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone || !formData.subject || !formData.message) return;
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (res.ok) {
        setSent(true);
        setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
        setTimeout(() => {
          setSent(false);
          setShowEmailForm(false);
        }, 2500);
      }
    } catch {
      /* silent */
    } finally {
      setSending(false);
    }
  };

  return (
    <footer
      className="bg-[#f5f0e8] text-stone-700 pt-10 pb-6 relative border-t border-stone-200"
      data-testid="footer-section"
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Top Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 pb-8 border-b border-stone-200">
          {/* Brand */}
          <div className="lg:col-span-2">
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className="inline-block mb-4"
              data-testid="footer-logo"
            >
              <span className="font-sans text-2xl sm:text-3xl lg:text-4xl font-[720] flex items-center gap-2">
                <img src="/logo.png" alt="New Coupling" className="h-10 w-auto" />
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-burgundy-700 via-burgundy-500 to-rose-400">
                  New Coupling
                </span>
              </span>
            </a>
            <p
              className="text-stone-600 text-sm leading-relaxed mb-4 max-w-sm"
              data-testid="footer-description"
            >
              글로벌 한인 중년을 위한 프리미엄 매칭 플랫폼.
              <br />
              배우고, 나누고, 사랑하세요.
              <br />
              당신의 새로운 인연이 여기 있습니다.
            </p>
            {/* Contact Info */}
            <div className="space-y-3">
              <div className="relative">
                <button
                  onClick={() => { setShowEmailForm(!showEmailForm); setSent(false); }}
                  className="flex items-center gap-3 text-stone-600 text-sm group cursor-pointer transition-all duration-200 hover:text-burgundy-500"
                  data-testid="footer-email-button"
                >
                  <Mail className="w-4 h-4 text-burgundy-400 group-hover:text-burgundy-500 transition-colors" />
                  <span className="border-b border-transparent group-hover:border-burgundy-500 transition-all duration-200">
                    contact@newcoupling.com
                  </span>
                </button>

                {/* Email Form Popup */}
                {showEmailForm && (
                  <div
                    className="absolute bottom-full left-0 mb-3 w-80 sm:w-96 bg-white border border-stone-200 rounded-xl shadow-2xl z-50 overflow-hidden"
                    data-testid="footer-email-form"
                  >
                    {/* Header */}
                    <div className="flex items-center justify-between px-5 py-3 bg-stone-50 border-b border-stone-200">
                      <div className="flex items-center gap-2">
                        <Send className="w-4 h-4 text-burgundy-500" />
                        <span className="text-sm font-semibold text-stone-800">문의하기</span>
                      </div>
                      <button
                        onClick={() => setShowEmailForm(false)}
                        className="text-stone-400 hover:text-stone-700 transition-colors"
                        data-testid="footer-email-form-close"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>

                    {sent ? (
                      <div className="p-8 text-center" data-testid="footer-email-success">
                        <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                          <Send className="w-5 h-5 text-green-600" />
                        </div>
                        <p className="text-green-600 font-medium text-sm">문의가 접수되었습니다!</p>
                        <p className="text-stone-500 text-xs mt-1">빠른 시일 내에 답변드리겠습니다.</p>
                      </div>
                    ) : (
                      <form onSubmit={handleSubmitContact} className="p-4 space-y-3" data-testid="footer-email-form-fields">
                        <input
                          type="text"
                          placeholder="이름"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:border-burgundy-300 transition-colors"
                          data-testid="contact-name-input"
                          required
                        />
                        <input
                          type="email"
                          placeholder="이메일"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:border-burgundy-300 transition-colors"
                          data-testid="contact-email-input"
                          required
                        />
                        <input
                          type="tel"
                          placeholder="연락처 (예: 010-1234-5678)"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:border-burgundy-300 transition-colors"
                          data-testid="contact-phone-input"
                          required
                        />
                        <input
                          type="text"
                          placeholder="제목"
                          value={formData.subject}
                          onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                          className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:border-burgundy-300 transition-colors"
                          data-testid="contact-subject-input"
                          required
                        />
                        <textarea
                          placeholder="문의 내용을 입력해 주세요..."
                          value={formData.message}
                          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                          rows={4}
                          className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:border-burgundy-300 transition-colors resize-none"
                          data-testid="contact-message-input"
                          required
                        />
                        <button
                          type="submit"
                          disabled={sending}
                          className="w-full bg-burgundy-500 hover:bg-burgundy-600 disabled:opacity-50 text-white font-semibold text-sm py-2.5 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
                          data-testid="contact-submit-button"
                        >
                          {sending ? (
                            <span>전송 중...</span>
                          ) : (
                            <>
                              <Send className="w-4 h-4" />
                              <span>보내기</span>
                            </>
                          )}
                        </button>
                      </form>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-start gap-3 text-stone-600 text-sm">
                <Phone className="w-4 h-4 text-burgundy-400 mt-0.5 flex-shrink-0" />
                <div>
                  <span>캐나다 토론토 1-647-292-3959</span><br />
                  <span>서울 010-9474-3959</span>
                </div>
              </div>
              <div className="flex items-start gap-3 text-stone-600 text-sm">
                <MapPin className="w-4 h-4 text-burgundy-400 mt-0.5 flex-shrink-0" />
                <div>
                  <span>캐나다 78 Westhampton Dr. Thornhill ON. L4J7J7</span><br />
                  <span>서울 서초구 강남대로 305, b117-01호(서초동)</span>
                </div>
              </div>
              {/* Company / Business Registration Info */}
              <div className="pt-3 mt-1 border-t border-stone-200/70 space-y-1.5 text-stone-500 text-xs leading-relaxed" data-testid="footer-company-info">
                <div>
                  <span className="font-semibold text-stone-600">캐나다</span> Liz TV Inc.
                  <span className="mx-1.5 text-stone-300">·</span>
                  사업자 번호 1465735-7
                </div>
                <div>
                  <span className="font-semibold text-stone-600">한국</span> 리즈티비 코리아 (Liz TV Korea)
                  <span className="mx-1.5 text-stone-300">·</span>
                  사업자 번호 781-01-03665
                </div>
              </div>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4
              className="font-semibold text-stone-800 mb-4"
              data-testid="footer-services-title"
            >
              서비스
            </h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    className="text-stone-500 text-sm hover:text-burgundy-500 transition-colors duration-200"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Curriculum */}
          <div>
            <h4
              className="font-semibold text-stone-800 mb-4"
              data-testid="footer-curriculum-title"
            >
              연애사관학교
            </h4>
            <ul className="space-y-3">
              {footerLinks.curriculum.map((link) => (
                <li key={link.name}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    className="text-stone-500 text-sm hover:text-burgundy-500 transition-colors duration-200"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4
              className="font-semibold text-stone-800 mb-4"
              data-testid="footer-support-title"
            >
              고객지원
            </h4>
            <ul className="space-y-3">
              {footerLinks.support.map((link) => (
                <li key={link.name}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    className="text-stone-500 text-sm hover:text-burgundy-500 transition-colors duration-200"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Legal / Policy Links Row */}
        <div className="pt-6 border-t border-stone-200/70" data-testid="footer-legal-links">
          <ul className="flex flex-wrap items-center justify-center md:justify-start gap-x-5 gap-y-2 text-[13px]">
            {[
              { name: "About Us", href: "/about", testid: "footer-link-about" },
              { name: "Contact", href: "__contact__", testid: "footer-link-contact" },
              { name: "Privacy Policy", href: "/privacy", testid: "footer-link-privacy" },
              { name: "Terms of Service", href: "/terms", testid: "footer-link-terms" },
              { name: "Refund Policy", href: "/refund-policy", testid: "footer-link-refund" },
              { name: "Community Guidelines", href: "/community-guidelines", testid: "footer-link-guidelines" },
            ].map((link, idx, arr) => (
              <li key={link.name} className="flex items-center gap-x-5">
                <button
                  onClick={() => {
                    if (link.href === "__contact__") {
                      setShowEmailForm(true);
                      setSent(false);
                      window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
                    } else {
                      navigate(link.href);
                      window.scrollTo(0, 0);
                    }
                  }}
                  className="text-stone-500 hover:text-burgundy-500 transition-colors duration-200"
                  data-testid={link.testid}
                >
                  {link.name}
                </button>
                {idx < arr.length - 1 && (
                  <span aria-hidden className="text-stone-300 hidden sm:inline">·</span>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Bottom Section */}
        <div className="pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p
            className="text-stone-500 text-sm"
            data-testid="footer-copyright"
          >
            © {currentYear} New Coupling. All rights reserved.
          </p>
          <div
            className="flex items-center gap-2 text-stone-500 text-sm"
            data-testid="footer-made-with"
          >
            <span>Made with</span>
            <Heart className="w-4 h-4 text-burgundy-400 fill-current" />
            <span>for Korean community</span>
          </div>
        </div>
      </div>
    </footer>
  );
};
